import {useMutation, useQueryClient} from '@tanstack/react-query'
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import {useLocation} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useAuth} from '../authentication/AuthProvider'
import {getIsOpenChat} from '../authentication/AuthUtils'
import {severityNotification} from '../notification/NotificationConstant'
import {
    hideNotification,
    showNotification,
} from '../notification/NotificationService'
import {getRoomById} from './ChatEndpoint'
import {
    chatEvent,
    onReceiverRoomMessageChange,
    removeChatListener,
    removeRoomMessage,
} from './ChatService'

const ChatContext = createContext({})

const MessageQueryKey = 'ChatMessages'

const chatType = 'chat_message'
const messageType = {
    text: 'text',
    image: 'image',
    separator: 'separator',
    name: 'name',
}

export const ChatProvider = React.memo(({children}) => {
    const location = useLocation()
    const queryClient = useQueryClient()
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const [activeParticipantId, setActiveParticipantId] = useState(null)
    const [activeRoom, setActiveRoom] = useState({})

    //#region Mutation for Chat
    const {mutate: mutateMessages} = useMutation(payload => payload, {
        onSuccess: payload => {
            if (payload && payload?.type === chatType && payload?.data) {
                onReceiverRoomMessageChange(payload)
                if (payload?.roomId === activeRoom?.id) {
                    queryClient.setQueryData(
                        [MessageQueryKey, user?.id, activeRoom?.id],
                        oldData => {
                            let item = {
                                id: payload?.id,
                                roomId: payload?.roomId,
                                participantId: payload?.participantId,
                                userId: payload?.userId,
                                avatar: payload?.avatar,
                                fullName: payload?.fullName,
                                data:
                                    payload.messageType === messageType.image
                                        ? payload.images
                                        : payload?.data,
                                createdAt: payload?.createdAt,
                                messageType: payload?.messageType,
                            }
                            return {
                                ...oldData,
                                content:
                                    !!oldData && oldData?.content.length > 0
                                        ? [...oldData.content, item]
                                        : [item],
                            }
                        },
                    )
                }
            }
        },
    })
    //#endregion

    const isOpenChat = getIsOpenChat()

    //#region Handle New Message
    const handleOnNewMessage = useCallback(
        messagePayload => {
            if (
                (userRole === SystemRole.USER && !getIsOpenChat()) ||
                (userRole !== SystemRole.USER &&
                    location.pathname !== '/conversations')
            ) {
                hideNotification()
                showNotification(
                    severityNotification.info,
                    'You have received a new chat message !',
                )
            } else {
                mutateMessages(messagePayload)
            }
        },
        [location.pathname, mutateMessages, userRole],
    ) // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion

    //#region Handle Change Active Room
    const handleChangeActiveRoom = useCallback(roomData => {
        if (roomData?.id) {
            setActiveRoom(roomData)
            if (roomData?.participantId) {
                setActiveParticipantId(roomData?.participantId)
            }
        } else {
            setActiveRoom({})
            setActiveParticipantId(null)
        }
    }, [])
    //#endregion

    //#region New Message Listener
    useEffect(() => {
        chatEvent.on('NEW-MESSAGE', handleOnNewMessage)
        chatEvent.on('MENU-CHANGE', () => handleChangeActiveRoom())

        return () => {
            removeChatListener()

            chatEvent.removeListener('MENU-CHANGE')
        }
    }, [handleOnNewMessage, handleChangeActiveRoom])
    //#endregion
    const getRoomData = useCallback(
        async roomId => {
            if (roomId) {
                const roomData = await getRoomById(user?.id, roomId)

                if (roomData?.id) {
                    setActiveRoom(roomData)
                    if (roomData?.participantId) {
                        setActiveParticipantId(roomData?.participantId)
                    }
                }
            }
        },
        [user?.id],
    )
    //#region New Message Listener
    useEffect(() => {
        chatEvent.on('NOTIFICATION-CHANGE', roomId => getRoomData(roomId))

        return () => {
            removeRoomMessage()
            chatEvent.removeListener('NOTIFICATION-CHANGE')
        }
    }, [handleOnNewMessage, handleChangeActiveRoom, getRoomData])
    //#endregion

    return (
        <ChatContext.Provider
            value={{
                activeParticipantId: activeParticipantId,
                activeRoom: activeRoom,
                changeActiveRoom: handleChangeActiveRoom,
            }}>
            {children}
        </ChatContext.Provider>
    )
})

export const useChatContext = () => useContext(ChatContext)
